// Google OAuth utility using Google Identity Services
// Minimal production-only client error reporter
const safeReportError = async (message, context = {}) => {
  try {
    if (process.env.NODE_ENV !== 'production') return;
    const payload = {
      message: String(message || 'unknown_error'),
      context: { location: typeof window !== 'undefined' ? (window.location?.pathname || '/') : '/', ...context },
      ts: Date.now(),
    };
    await fetch('/api/client-logs', {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify(payload),
      keepalive: true,
    });
  } catch (_) {}
};
class GoogleAuth {
  constructor() {
    this.clientId = process.env.REACT_APP_GOOGLE_CLIENT_ID;
    this.tokenClient = null;
    this.isInitialized = false;
  }

  // Initialize Google Identity Services
  async initialize() {
    if (this.isInitialized) return;
    // Validate clientId in production
    if (!this.clientId) {
      safeReportError('google_client_id_missing');
      if (process.env.NODE_ENV === 'production') {
        throw new Error('google_client_id_missing');
      }
    }

    return new Promise((resolve, reject) => {
      const start = Date.now();
      const maxWaitMs = 10000; // 10s timeout
      // Wait for Google Identity Services to load with timeout
      const checkGoogleLoaded = () => {
        if (typeof window !== 'undefined' && window.google && window.google.accounts) {
          this.isInitialized = true;
          resolve();
        } else if (Date.now() - start > maxWaitMs) {
          safeReportError('google_initialize_timeout');
          reject(new Error('google_initialize_timeout'));
        } else {
          setTimeout(checkGoogleLoaded, 100);
        }
      };
      checkGoogleLoaded();
    });
  }

  // Create Google Sign-In button
  createSignInButton(elementId, callback) {
    if (!this.isInitialized) {
      // Google Auth not initialized
      return;
    }

    try {
      this.tokenClient = window.google.accounts.oauth2.initTokenClient({
        client_id: this.clientId,
        scope: 'email profile openid',
        callback: callback,
      });
    } catch (error) {
      safeReportError('google_create_button_failed');
    }
  }

  // Trigger Google Sign-In
  async signIn() {
    // Ensure GIS is ready
    try {
      await this.initialize();
    } catch (_) {
      return null;
    }

    // Lazily create the token client if it doesn't exist yet
    if (!this.tokenClient) {
      try {
        this.tokenClient = window.google.accounts.oauth2.initTokenClient({
          client_id: this.clientId,
          scope: 'openid email profile',
          callback: () => {}, // will be replaced below per request
        });
      } catch (error) {
        safeReportError('google_init_token_client_failed');
        return null;
      }
    }

    return new Promise((resolve, reject) => {
      try {
        // Set per-request callback BEFORE requesting the token
        this.tokenClient.callback = (response) => {
          if (response && response.error) {
            reject(new Error(response.error));
          } else {
            resolve(response);
          }
        };

        this.tokenClient.requestAccessToken({ prompt: 'select_account' });
      } catch (error) {
        safeReportError('google_request_access_token_failed');
        reject(error);
      }
    });
  }

  // Get user info from Google
  async getUserInfo(accessToken) {
    try {
      if (typeof accessToken !== 'string' || accessToken.length < 10 || accessToken.length > 4096) {
        safeReportError('google_access_token_invalid');
        throw new Error('google_access_token_invalid');
      }
      const url = 'https://www.googleapis.com/oauth2/v2/userinfo';
      const response = await fetch(url, {
        method: 'GET',
        headers: {
          Authorization: `Bearer ${accessToken}`,
        },
      });
      if (!response.ok) {
        safeReportError('google_userinfo_fetch_failed', { status: response.status });
        throw new Error('Failed to fetch user info');
      }
      return await response.json();
    } catch (error) {
      throw error;
    }
  }

  // Verify ID token on backend
  async verifyIdToken(idToken) {
    try {
      if (typeof idToken !== 'string' || idToken.length < 10 || idToken.length > 8192) {
        safeReportError('google_id_token_invalid');
        throw new Error('google_id_token_invalid');
      }
      const response = await fetch('/api/auth/google/verify', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({ idToken }),
      });

      if (!response.ok) {
        safeReportError('google_verify_id_token_failed', { status: response.status });
        throw new Error('Failed to verify ID token');
      }

      return await response.json();
    } catch (error) {
      throw error;
    }
  }
}

// Create singleton instance
const googleAuth = new GoogleAuth();

export default googleAuth;
